import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@pc/store';
import { computed } from '@angular/core';
import { getNewTraveler, Traveler, TravelerType } from '../../shared/models/traveler.interface';
import { v4 as uuidv4 } from 'uuid';

interface ITravelerState {
  allTravelers: Traveler[];
  isNewCustomer: boolean;
  isLoading: boolean;
  stateId: number;
  isOneMemberOnly: boolean;
}

const initialState: ITravelerState = {
  allTravelers: [],
  isNewCustomer: true,
  isLoading: false,
  stateId: 0,
  isOneMemberOnly: false
};

export const TravelersStore = signalStore(
  { providedIn: 'root' },
  withDevtools('TravelersStore'),
  withState(initialState),
  withComputed((state) => ({
    adultTravelers: computed(() => state.allTravelers().filter((traveler) => traveler.type === TravelerType.ADULT)),
    childTravelers: computed(() => state.allTravelers().filter((traveler) => traveler.type !== TravelerType.ADULT))
  })),

  withComputed((state) => ({
    adultsCounter: computed(() => state.adultTravelers().length),
    childrenCounter: computed(() => state.childTravelers().length),
    sortedAllTravelers: computed(() => state.adultTravelers().concat(state.childTravelers()))
  })),
  withMethods((store) => ({
    addTraveler(traveler: Traveler): void {
      const travelerWithID = (traveler = { ...traveler, id: uuidv4() });
      patchState(store, (state) => ({ allTravelers: [...state.allTravelers, travelerWithID] }));
    },

    removeTraveler(id: string): void {
      patchState(store, (state) => ({ allTravelers: state.allTravelers.filter((t) => t.id !== id) }));
    },

    updateTraveler(updatedTraveler: Traveler): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          if (traveler.id === updatedTraveler.id) {
            return updatedTraveler;
          }
          return traveler;
        })
      }));
    },

    getHofTraveler(): Traveler {
      let hofTraveler = store.allTravelers()[0];

      if (!hofTraveler) {
        hofTraveler = getNewTraveler(TravelerType.ADULT);
        patchState(store, (state) => {
          const updatedTravelers = [...state.allTravelers];
          updatedTravelers[0] = hofTraveler;
          return { allTravelers: updatedTravelers };
        });
      }

      return hofTraveler;
    },

    addStateId(stateId: number): void {
      patchState(store, { stateId: stateId });
    },

    setNumOfParticipants(numOfParticipants: number): void {
      patchState(store, {
        allTravelers: Array.from({ length: numOfParticipants }, () => getNewTraveler(TravelerType.ADULT))
      });
    },
    setIsOneMemberOnly(isOneMemberOnly: boolean): void {
      patchState(store, { isOneMemberOnly });
    },
    addProduct(productID: number): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          const productExists = traveler.productCover.some((product) => product.productID === productID);

          const updatedProductCover = productExists
            ? traveler.productCover
            : [...traveler.productCover, { productID, coversIds: [] }];

          return { ...traveler, productCover: updatedProductCover };
        })
      }));
    },

    addCoverToTraveler(productID: number, coverID: number, travelerID: string): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          if (traveler.id === travelerID) {
            const product = traveler.productCover?.find((product) => product.productID === productID);
            if (product && !product.coversIds.includes(coverID)) {
              product.coversIds = [...product.coversIds, coverID];
            }
          }
          return traveler;
        })
      }));
    },

    addCoverToAllTravelers(productID: number, coverID: number): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          const product = traveler.productCover?.find((product) => product.productID === productID);
          if (product && !product.coversIds.includes(coverID)) {
            product.coversIds = [...new Set([...product.coversIds, coverID])];
          }
          return traveler;
        })
      }));
    },

    removeCoverFromTraveler(productID: number, coverID: number, travelerID: string): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          if (traveler.id === travelerID) {
            const product = traveler.productCover?.find((product) => product.productID === productID);
            if (product) {
              product.coversIds = product.coversIds.filter((id) => id !== coverID);
            }
          }
          return traveler;
        })
      }));
    },

    removeCoverFromAllTravelers(productID: number, coverID: number): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((traveler) => {
          const product = traveler.productCover?.find((product) => product.productID === productID);
          if (product) {
            product.coversIds = product.coversIds.filter((id) => id !== coverID);
          }
          return traveler;
        })
      }));
    },

    clearUnAddedTravelers(): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.filter((t) => t.age >= 0)
      }));
    },

    isTravelerExist(id: string): boolean {
      const traveler = store.allTravelers().find((t) => t.id === id);
      return !!traveler && traveler.age >= 0;
    },

    isTravelerDataFilled(id: string): boolean {
      const traveler = store.allTravelers().find((t) => t.id === id);
      return !!traveler?.firstName;
    },

    clearFakeDOB(): void {
      patchState(store, (state) => ({
        allTravelers: state.allTravelers.map((t) => ({ ...t, dateOfBirth: t.firstName ? t.dateOfBirth : undefined }))
      }));
    },

    updateParticipantNum(): void {
      const travelers = store.sortedAllTravelers();
      let index = 0;

      travelers.forEach((t) => {
        t.participantNum = index;
        ++index;
      });
    }
  }))
);
